/**
 * Centralized permissions hook for Serac Portal.
 *
 * Single auth model after the SI/customer demolition (2026-04-27):
 * - Portal users (Individual/Teams via self-serve, Enterprise via admin elevation)
 * - Admin / Marketing (separate auth)
 * - SSO (federated portal)
 *
 * Usage:
 *   const { role, hasActiveSubscription, isTrialing, canWrite, planLabel } = usePermissions();
 */

import { useAuth } from '../contexts/AuthContext';

export type AuthRole =
  | 'admin'
  | 'marketing'
  | 'portal_owner'
  | 'portal_stakeholder'
  | null;

export function usePermissions() {
  const {
    session,
    portalSession,
    isPortalAuthenticated,
    isAdminAuthenticated,
    isLoading,
  } = useAuth();

  // === Canonical Role ===
  // Admin ALWAYS wins — prevents stale marketing/portal sessions from overriding admin.
  const role: AuthRole = isAdminAuthenticated
    ? 'admin'
    : (session?.role as AuthRole) ?? null;

  // === Role Identity ===
  const isAdmin = isAdminAuthenticated;
  const isMarketingUser = role === 'marketing';

  // === Plan shape vs. tier ===
  // `planType` maps `organizations.plan_type`, which is enum('individual','team')
  // under STRICT_TRANS_TABLES — writing 'enterprise' is errno 1265, so the column
  // CANNOT hold that value and every production org sits on 'individual'/'team'.
  // Deriving the tier from it (the old `planType === 'enterprise'`) therefore made
  // `isEnterpriseTier` structurally false for every account, including paid
  // enterprise contracts, which hid all four /governance pages from the people who
  // bought them while the APIs served them fine. plan_type now only describes the
  // SEAT SHAPE; the tier comes from the dedicated column below.
  const planType =
    session?.organization?.planType || portalSession?.organization?.planType;

  // === Subscription & Plan State ===
  const subscription = session?.subscription || portalSession?.subscription;
  const hasLiveSubscription =
    !!subscription && ['trialing', 'active', 'past_due'].includes(subscription.status);

  // Trial = unexpired trial. Mirrors the backend's `isActiveTrial`
  // in utils/modules.ts so frontend gating matches backend behaviour.
  const isTrialing =
    subscription?.status === 'trialing' &&
    (!subscription.trialEndsAt || subscription.trialEndsAt > Date.now());

  // --- Explicit tier pins, in the order the backend stores them ---------------
  // 1. `organizations.tier` (migration 129) — the admin-set contract tier and the
  //    ONLY place 'enterprise' can live. An elevated org frequently has no
  //    `subscriptions` row at all, so this wins outright.
  // 2. `subscriptions.tier` — the self-serve flat-bundle pin ('solo'|'pro') set by
  //    the tier checkout. Honoured only while the subscription is actually live:
  //    a canceled row keeps its old pin, and trusting it would show Pro nav that
  //    the API answers with 403.
  // `session` is the normalized shape, `portalSession` the raw login response —
  // prefer the normalized one and fall back so a pre-normalization session resolves.
  const TIER_ORDER = ['free', 'solo', 'pro', 'team', 'enterprise'] as const;
  type Tier = (typeof TIER_ORDER)[number];
  const asTier = (v: unknown): Tier | null =>
    typeof v === 'string' && (TIER_ORDER as readonly string[]).includes(v) ? (v as Tier) : null;
  // `tier` is not declared on the api/auth.ts PortalOrganization/PortalSubscription
  // types yet, so the raw login response is read through `unknown`.
  const rawTier = (o: unknown): unknown =>
    o && typeof o === 'object' ? (o as { tier?: unknown }).tier : undefined;

  const orgTier =
    asTier(session?.organization?.tier) ?? asTier(rawTier(portalSession?.organization));
  const subTier = hasLiveSubscription
    ? asTier(session?.subscription?.tier) ?? asTier(rawTier(portalSession?.subscription))
    : null;
  const explicitTier = orgTier ?? subTier;

  // `planType === 'enterprise'` is kept purely as a fallback for session shapes
  // that predate the tier field (it can never be true against the live schema).
  const isEnterpriseTier = explicitTier === 'enterprise' || planType === 'enterprise';

  const hasActiveSubscription =
    // Enterprise-tier orgs always have full access (admin-elevated, custom contract).
    isEnterpriseTier
    // Portal subs need an actual active subscription.
    || hasLiveSubscription
    // Not a portal user — no subscription needed (admin/marketing).
    || (!isPortalAuthenticated && !session);

  const isFree =
    (role === 'portal_owner' || role === 'portal_stakeholder') &&
    !isLoading &&
    !hasActiveSubscription;
  const isPaid = subscription?.status === 'active';
  const isPastDue = subscription?.status === 'past_due';

  // Trial-aware module check. Returns true when the module is
  // explicitly active OR the account is on an unexpired trial — the
  // same "trial = everything unlocked" principle the backend enforces
  // in `isFeatureActive` / `effectiveFeatures`. Prefer this over
  // `subscription?.activeModuleIds?.includes(...)` anywhere a paywall
  // / upgrade prompt is gated.
  const hasModule = (moduleId: string): boolean => {
    if (isEnterpriseTier) return true;
    if (isTrialing) return true;
    return !!subscription?.activeModuleIds?.includes(moduleId);
  };

  // Feature-level check — the CAPABILITY counterpart of `hasModule`.
  // Reads the `activeFeatures` list the backend puts on the session (the same
  // `effectiveFeatures` helper that fills the JWT), so a UI gate tests the
  // exact string the route tests server-side (e.g. `pro-chat` in chat.ts).
  //
  // Use this for capability gates (chat, sandbox, plan mode): capability is
  // baseline for every tier — the wall is SCALE, not capability — while
  // `activeModuleIds` only says what was PURCHASED, so module-based gating
  // locks out every account without a `subscriptions` row.
  //
  // Missing list = the session predates the field (older backend, SSO /
  // impersonation session shapes) — NOT "no entitlements" and NOT "everything".
  // We fall back to the capability baseline below, which is what the backend's
  // `isFeatureActive` returns for these strings regardless of the stored list.
  // That also decides what the user sees in the second before the session
  // resolves: the baseline chat controls, never a paid-only affordance that
  // pops in and then disappears.
  // `session` is the normalized shape, `portalSession` the raw login response —
  // both carry the field; prefer the normalized one and fall back so a session
  // restored before normalization still resolves.
  const activeFeatures = session?.activeFeatures ?? portalSession?.activeFeatures;
  // Mirrors the backend FREE_TIER_FEATURES exactly:
  // `deriveFeatures(['base', 'pro-tier', 'instance-graph'])` over
  // MODULE_TO_FEATURES (utils/modules.ts). The 2026-06 capability inversion
  // made all of it baseline for every account, Free included — Free is bounded
  // by SCALE, never by capability.
  //
  // This list used to carry only the three `pro-tier` strings, which is a
  // GATING BUG and not a conservative default: it is the answer for any session
  // whose `activeFeatures` list has not arrived (SSO / impersonation shapes,
  // and the first paint of every page load), and the backend answers those same
  // eleven other strings with 200 the whole time. Keep in sync with
  // FREE_TIER_MODULE_IDS.
  const BASELINE_FEATURES = [
    // base
    'activity-tracking',
    'git-local',
    'dashboard',
    'instance-chat',
    'team-skills',
    'team-analytics',
    'shared-credentials',
    'custom-skills',
    'dedicated-support',
    'sla',
    // pro-tier
    'pro-chat',
    'virtual-fs',
    'native-tools',
    // instance-graph
    'instance-graph',
  ];
  const hasFeature = (feature: string): boolean =>
    activeFeatures
      ? activeFeatures.includes(feature)
      : BASELINE_FEATURES.includes(feature);

  // === Resolved hosted tier ===
  // Same ladder AND the same inputs as the backend's `resolveHostedTier`
  // (utils/modules.ts): an explicit tier pin wins, otherwise the tier is inferred
  // from subscription status + the effective FEATURE list — never from module ids,
  // which only say what was purchased.
  //
  // One deliberate divergence, documented rather than silently copied: the backend
  // maps `past_due` to 'free'. It uses that for SCALE limits (instance cap, VFS
  // quota), where a dunning org should not keep growing. Doing it here would strip
  // the whole paid UI mid-dunning while `portalPaidSubscriptionGuard`
  // (middleware/module-access.ts) still answers past_due with 200 — i.e. the menu
  // would gate STRICTER than the API. past_due therefore stays live for the UI.
  const ENTERPRISE_TIER_FEATURES = ['sso-scim', 'policy-engine', 'multi-instance', 'ai-audit'];
  const PRO_TIER_FEATURES = ['code-review', 'sn-patterns'];
  // Only used when the session carries no activeFeatures list at all (SSO /
  // impersonation shapes): fall back to the consultant-toolkit MODULE ids. Keep
  // in sync with the backend CONSULTANT_TOOLKIT_MODULE_IDS.
  const PRO_TIER_MODULE_IDS = ['code-review', 'sn-code-intelligence', 'github', 'gitlab', 'jira', 'azdo', 'priority-support'];

  const featureSaysEnterprise =
    !!activeFeatures && ENTERPRISE_TIER_FEATURES.some((f) => activeFeatures.includes(f));
  const featureSaysPro = activeFeatures
    ? PRO_TIER_FEATURES.some((f) => activeFeatures.includes(f))
    : (subscription?.activeModuleIds || []).some((id: string) => PRO_TIER_MODULE_IDS.includes(id));

  // Backend-faithful ladder: `resolveHostedTier` (utils/modules.ts) reads the
  // subscription status plus the effective feature list, and nothing else.
  const hostedTier: Tier =
    !hasActiveSubscription ? 'free'
    : isTrialing ? 'pro'                 // reverse-trial = full Pro
    : featureSaysEnterprise ? 'enterprise'
    : featureSaysPro ? 'pro'
    : 'solo';                            // active sub, base only → Solo

  // UI-only refinement on top of that ladder: plan_type is the seat SHAPE, not
  // the tier, but a 'team' org really is Team and the backend has no feature
  // string that can infer it. Deliberately kept OUT of the seat math further
  // down — `resolveSeatUsage` (routes/team.ts) stopped reading plan_type, so a
  // plan_type-derived tier must not promise seats the invite API refuses.
  const inferredTier: Tier =
    planType === 'team' && !isTrialing && (hostedTier === 'solo' || hostedTier === 'pro')
      ? 'team'
      : hostedTier;

  const tier: Tier = explicitTier ?? inferredTier;

  // Tier-ladder check: "is this account at least <min>?" The ladder is
  // ordered free < solo < pro < team < enterprise — mirrors the backend
  // TIER_PRESETS (utils/modules.ts), where each tier's includedModuleIds
  // is a superset of the one below. Use this for tier-gated UI
  // (consultant toolkit incl. code intelligence / upgrade simulator =
  // hasTier('pro'), org-governance = hasTier('enterprise')) instead of
  // comparing tier strings inline.
  const hasTier = (min: Tier): boolean =>
    TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf(min);

  // Team-SHAPED orgs — the label and the team-only UI, not a seat count (that is
  // `hasMultiSeat` below). Enterprise contracts are stored with plan_type 'team'
  // (applyTierPreset clamps it, since the enum has no 'enterprise'), and the tier
  // ladder covers the rest.
  const isTeamUser = tier === 'team' || tier === 'enterprise' || planType === 'team';
  const isIndividualUser =
    (role === 'portal_owner' || role === 'portal_stakeholder') && !isTeamUser;

  // === Plan Label (for sidebar badge) ===
  const planLabel = isAdmin
    ? 'Admin'
    : isMarketingUser
    ? 'Marketing'
    : isEnterpriseTier
    ? session?.organization?.name || portalSession?.organization?.name || 'Enterprise'
    : isLoading
    ? '...'
    : subscription && ['trialing', 'active', 'past_due'].includes(subscription.status)
    ? `${isTeamUser ? 'Team' : 'Individual'}${isTrialing ? ' (Trial)' : ''}`
    : 'Free';

  // === Permissions ===
  const canRead = true;
  const canWrite = !isMarketingUser;

  const membershipRole = session?.membershipRole || portalSession?.role;
  const isStakeholder = membershipRole === 'stakeholder';
  const hasDeveloperAccess = !isStakeholder;

  // === Seats ===
  // "Can this account have more than one developer?" — mirrors `resolveSeatUsage`
  // in routes/team.ts, the resolution POST /api/portal/team/invite enforces:
  //   - team/enterprise are negotiated contracts, so the preset (unlimited) wins
  //     over whatever the row says;
  //   - every other tier takes the stored row FLOORED at the purchased bundle:
  //     max(subscriptions.developer_seats, TIER_PRESETS[pinned tier].includedSeats);
  //   - plan_type grants no seats at all — the backend stopped reading it, and
  //     so does this gate.
  // Gate team UI on this, NOT on `isTeamUser`. Pro is sold as 3 seats while a
  // self-serve Pro org stays on plan_type 'individual', and Pro rows written
  // before the flat-tier checkout still carry developer_seats = 1 — reading the
  // raw row alone left exactly those paying customers with no menu route to
  // /portal/team while the API accepted their invites.
  //
  // The allowances below are a hand-kept copy of TIER_PRESETS.includedSeats
  // (backend utils/modules.ts). The authoritative numbers are the `seats` block
  // of GET /api/portal/team/members, but this hook renders on every page and
  // must not fire a request, and the session carries the tier without its seat
  // allowance. Keep in sync with TIER_PRESETS; -1 = unlimited.
  const TIER_INCLUDED_SEATS: Record<Tier, number> = {
    free: 1,
    solo: 1,
    pro: 3,
    team: -1,
    enterprise: -1,
  };
  // Same inputs as the backend: the pinned tier if there is one, otherwise the
  // status+features inference — never the plan_type refinement.
  const seatTier: Tier = explicitTier ?? hostedTier;
  // Mirrors resolveSeatUsage in routes/team.ts line for line, because a menu
  // that disagrees with the invite endpoint is the bug this wave is undoing.
  //
  // 1. The bundle, keyed on the RESOLVED tier rather than on an explicit pin.
  //    A reverse trial carries no pin, so keying on the pin made this whole
  //    floor a no-op for exactly the rows it exists for: a trial, and a Pro
  //    row written before the tier columns that still says developer_seats 1.
  const bundleSeats = TIER_INCLUDED_SEATS[seatTier];
  // 2. The billing row, which can exceed the bundle (seats bought on top) or
  //    lag it. Without a live subscription there is no row to read.
  const rowSeats = hasLiveSubscription ? subscription?.developerSeats ?? 1 : 1;
  // Larger of the two wins; -1 (unlimited) beats any finite number. Team and
  // Enterprise resolve to -1 through the bundle, so they need no special case.
  const developerSeatCap =
    bundleSeats < 0 || rowSeats < 0 ? -1 : Math.max(bundleSeats, rowSeats);
  const hasMultiSeat = developerSeatCap < 0 || developerSeatCap > 1;

  // === Team Features ===
  // Team-shaped orgs (team/enterprise tier, or a legacy plan_type 'team' org)
  // get the team-only UI. Anything bound to SEATS gates on `hasMultiSeat`.
  const hasTeamFeatures = isTeamUser;

  return {
    role,
    isAdmin,
    isMarketingUser,
    isEnterpriseTier,
    isPortalAuthenticated,
    isTeamUser,
    isIndividualUser,

    hasActiveSubscription,
    isFree,
    isTrialing,
    isPaid,
    isPastDue,
    planLabel,
    tier,
    hasTier,
    subscription,

    canRead,
    canWrite,
    hasDeveloperAccess,
    hasMultiSeat,
    developerSeatCap,
    hasTeamFeatures,
    hasModule,
    hasFeature,
    isStakeholder,
    membershipRole,

    isLoading,
  };
}
